// Burrow / Submerge mechanics. Pure. No React, no Three.js. The
// GameBoard side wraps these with prompt + animation + sync calls.
//
// ── Sorcery rules (codex "Burrowing", "Submerge", "Underground",
//                   "Underwater", "Surface", "Subsurface", "Step",
//                   "Waterbound", "Landbound") ──
//
//   • A minion with Burrowing may be summoned to underground locations
//     and survive there. Step rule: it may move between surface and
//     subsurface of a LAND site, or between adjacent underground
//     locations.
//   • A minion with Submerge may be summoned to underwater locations
//     and survive there. Step rule: it may move between surface and
//     subsurface of a WATER site, or between adjacent underwater
//     locations.
//   • A minion with BOTH abilities can move directly between under-
//     ground and underwater in adjacent cells.
//   • A minion WITHOUT Burrowing that ends up underground (e.g. forced
//     by Bury) dies. A minion without Submerge underwater dies.
//   • Avatars never enter the underground or underwater.
//   • Forced burrow / submerge effects fail if the target occupies a
//     site whose region the forced ability can't survive in. E.g.
//     Bury can't burrow a card already on a water-only site.
//   • Flooded sites are water sites. A site can be both water AND
//     another element (Flooded Humble Village).
//
// ── card.subsurface ──
// Per-instance state on the minion. One of:
//   null. On the surface (default)
//   'underground'. Burrowed
//   'underwater'. Submerged
// Wiped by lifecycle.resetCardOnLeavePlay so re-played cards arrive
// on the surface.

import { regionsAtCell } from './regions';

const SURFACE     = 'surface';
const UNDERGROUND = 'underground';
const UNDERWATER  = 'underwater';

// ── Keyword presence helpers ─────────────────────────────────────
// Suppression-aware via the shared keywordQuery helper, so the radial
// menu's manual-off path can disable an otherwise-printed keyword.

import { hasKeyword } from './keywordQuery';
import { countsAsMinion } from './typeline';

export function hasBurrowing(card) { return hasKeyword(card, 'burrowing'); }
export function hasSubmerge(card)  { return hasKeyword(card, 'submerge'); }

// ── Artifact exception ───────────────────────────────────────────
// Artifacts may always be moved below the surface manually, keyword
// or not. Two reasons:
//   • Codex: the subsurface death rule names MINIONS ("Whenever a
//     minion without Burrowing occupies an underground location, it
//     immediately dies"). Artifacts persist underground/underwater.
//   • Band-aid for unautomated cards: effects that burrow/submerge
//     artifacts (carried treasure, buried relics) aren't all wired
//     yet, so the manual action-menu path must allow it.

export function isArtifact(card) { return (card?.type || '') === 'Artifact'; }

// ── Region availability at a cell ────────────────────────────────

export function cellHasUnderground(meshes, cellAt, col, row, getFullCard) {
  return regionsAtCell(meshes, cellAt, col, row, getFullCard).has(UNDERGROUND);
}

export function cellHasUnderwater(meshes, cellAt, col, row, getFullCard) {
  return regionsAtCell(meshes, cellAt, col, row, getFullCard).has(UNDERWATER);
}

export function cellHasSurface(meshes, cellAt, col, row, getFullCard) {
  return regionsAtCell(meshes, cellAt, col, row, getFullCard).has(SURFACE);
}

// ── Per-card subsurface state predicates ─────────────────────────

export function isOnSurface(card)    { return !card?.subsurface; }
export function isBurrowed(card)     { return card?.subsurface === UNDERGROUND; }
export function isSubmerged(card)    { return card?.subsurface === UNDERWATER; }
export function isSubsurface(card)   { return !!card?.subsurface; }

// ── Action eligibility ───────────────────────────────────────────
// All of these answer the question "can the player click this
// action right now." UI uses them to decide which menu items to
// show. The verb actions themselves (burrow/submerge/surface) live
// in GameBoard since they touch state + sync.

/**
 * Can this card BURROW from its current cell? Requires:
 *   • Card has Burrowing keyword (or is an Artifact. See exception)
 *   • Card currently on surface
 *   • The cell exposes an underground region (i.e. has a land site).
 */
export function canBurrow(card, meshes, cellAt, col, row, getFullCard) {
  if (!hasBurrowing(card) && !isArtifact(card)) return false;
  if (!isOnSurface(card)) return false;
  return cellHasUnderground(meshes, cellAt, col, row, getFullCard);
}

/**
 * Can this card SUBMERGE from its current cell? Requires:
 *   • Card has Submerge keyword (or is an Artifact. See exception)
 *   • Card currently on surface
 *   • The cell exposes an underwater region (water or Flooded site).
 */
export function canSubmerge(card, meshes, cellAt, col, row, getFullCard) {
  if (!hasSubmerge(card) && !isArtifact(card)) return false;
  if (!isOnSurface(card)) return false;
  return cellHasUnderwater(meshes, cellAt, col, row, getFullCard);
}

/**
 * Can this card SURFACE? Requires:
 *   • Card currently below the surface
 *   • The cell exposes a surface (has any site)
 * No keyword check. Surfacing is universal (any minion can come up).
 */
export function canSurface(card, meshes, cellAt, col, row, getFullCard) {
  if (!isSubsurface(card)) return false;
  return cellHasSurface(meshes, cellAt, col, row, getFullCard);
}

// ── Forced bury / drown survival check ──────────────────────────
//
// "When an effect 'burrows' a card, you forcefully move that card
// from the surface to underground, even if it doesn't have the
// Burrowing ability."  → forced burrow lands the card UNDERGROUND
// regardless of keyword. Then per Underground rule: "Whenever a
// minion without Burrowing occupies an underground location, it
// immediately dies." So a forced burrow on a card without Burrowing
// = bury then immediate death.
//
// Symmetric for forced submerge: no Submerge → dies underwater.
//
// Both death rules name MINIONS. Artifacts survive below the
// surface regardless of keywords (see Artifact exception above).

export function survivesForcedBury(card)    { return hasBurrowing(card) || isArtifact(card); }
export function survivesForcedSubmerge(card) { return hasSubmerge(card) || isArtifact(card); }

// ── On-play placement ────────────────────────────────────────────

/**
 * Decide which placement choices to offer when a Minion is played
 * onto cell (col, row). Returns an array of placement options:
 *   { region: 'surface' | 'underground' | 'underwater', label }
 * Empty array means the play should fall through to the existing
 * surface-default flow (no prompt needed).
 *
 * Rules:
 *   • A unit with Burrowing on a LAND site can choose surface OR
 *     burrowed (underground).
 *   • A unit with Submerge on a WATER site can choose surface OR
 *     submerged (underwater).
 *   • A unit with BOTH abilities played onto a cell with both a
 *     land AND water site (sandbox dual-site case) can choose all
 *     three.
 *   • Avatars never get the prompt. They can't enter underground
 *     or underwater.
 *   • Cards without either keyword get the empty default (surface).
 */
export function placementChoicesForPlay(card, fullCard, meshes, cellAt, col, row, getFullCard) {
  if (!card) return [];
  const type = fullCard?.type || card.type;
  if (type === 'Avatar') return [];
  // countsAsMinion: an Automaton with Burrowing/Submerge gets the
  // same subsurface placement choice as any minion.
  if (!countsAsMinion(fullCard || card)) return [];

  const canBurrowHere = hasBurrowing(card) && cellHasUnderground(meshes, cellAt, col, row, getFullCard);
  const canSubmergeHere = hasSubmerge(card) && cellHasUnderwater(meshes, cellAt, col, row, getFullCard);

  if (!canBurrowHere && !canSubmergeHere) return [];

  const out = [];
  // Surface is always an option when a subsurface placement is.
  if (cellHasSurface(meshes, cellAt, col, row, getFullCard)) {
    out.push({ region: SURFACE, label: 'Surface' });
  }
  if (canBurrowHere) out.push({ region: UNDERGROUND, label: 'Burrowed' });
  if (canSubmergeHere) out.push({ region: UNDERWATER, label: 'Submerged' });
  return out;
}

// ── Movement gating ──────────────────────────────────────────────

/**
 * When a subsurface unit moves to a different cell, what's the
 * outcome? Returns one of:
 *   { kind: 'preserve' }
 * Destination supports the same region; subsurface state stays.
 *   { kind: 'surface' }
 * Destination supports surface but NOT the unit's region;
 *       force-surface on arrival (auto-pop-up).
 *   { kind: 'die' }
 * Destination is a void without the right keywords. The unit
 *       would die. Caller should block the move or kill the unit.
 *
 * A unit with BOTH Burrowing and Submerge can cross underground →
 * underwater (and vice versa) in a single step per the codex
 * "Burrowing" / "Submerge" cross-region clause; we treat that as
 * 'preserve' but swap the region.
 *   { kind: 'preserve', region: 'underwater' }. Region swap
 *
 * Surface-on-surface moves don't go through this function (they're
 * handled by the existing drag/drop code).
 */
export function moveOutcome(card, meshes, cellAt, toCol, toRow, getFullCard) {
  if (!isSubsurface(card)) return { kind: 'preserve' };
  const regions = regionsAtCell(meshes, cellAt, toCol, toRow, getFullCard);

  const dest = card.subsurface;
  if (regions.has(dest)) {
    return { kind: 'preserve', region: dest };
  }

  // Burrowing + Submerge can swap regions in one step.
  const dual = hasBurrowing(card) && hasSubmerge(card);
  if (dual) {
    if (dest === UNDERGROUND && regions.has(UNDERWATER)) {
      return { kind: 'preserve', region: UNDERWATER };
    }
    if (dest === UNDERWATER && regions.has(UNDERGROUND)) {
      return { kind: 'preserve', region: UNDERGROUND };
    }
  }

  // Destination doesn't support our subsurface. Try to surface.
  if (regions.has(SURFACE)) return { kind: 'surface' };

  // Void cell, no surface, no matching subsurface → unit dies if it
  // doesn't have Voidwalk. Voidwalk is handled in a separate path
  // (different module / future work); for now we report 'die'.
  return { kind: 'die' };
}

// ── Forced effects (Bury / Drown / etc.) ─────────────────────────

/**
 * Can a "forced burrow" effect (e.g. Bury) succeed against this
 * card at this cell? Per the codex: the effect FAILS if the target
 * already occupies at least one water site. The card doesn't need
 * the Burrowing keyword. Forced burrow works regardless.
 */
export function canForceBurrow(card, meshes, cellAt, col, row, getFullCard) {
  if (!cellHasUnderground(meshes, cellAt, col, row, getFullCard)) return false;
  // "If an effect tries to forcefully burrow a card that occupies at
  // least one water site, the effect fails." We treat the cell as
  // "occupies a water site" when the cell hosts an underwater region.
  const regions = regionsAtCell(meshes, cellAt, col, row, getFullCard);
  if (regions.has(UNDERWATER)) return false;
  return true;
}

/**
 * Can a "forced submerge" effect (e.g. Drown) succeed? Symmetric
 * to canForceBurrow. Fails if the cell hosts a land site.
 */
export function canForceSubmerge(card, meshes, cellAt, col, row, getFullCard) {
  if (!cellHasUnderwater(meshes, cellAt, col, row, getFullCard)) return false;
  const regions = regionsAtCell(meshes, cellAt, col, row, getFullCard);
  if (regions.has(UNDERGROUND)) return false;
  return true;
}

// ── Oversized-unit footprint guards ──────────────────────────────
//
// Codex: an oversized unit (a card occupying more than one cell. E.g.
// an Enchantress-animated aura with a 2-cell INTERSECTION_2 footprint)
// must be ENTIRELY within a region to be forced under. Straddling a
// water site and a land site, it can be neither submerged nor buried.
// the forced effect fizzles and the unit stays on the surface instead
// of drowning. The single-cell helpers above already encode "this cell
// supports the region"; these fold that test across every covered cell.
// The occupied-cell list comes from `unitFootprintCells` (footprint.js).

/** Forced submerge succeeds only if EVERY occupied cell is water. */
export function canForceSubmergeAll(card, cells, meshes, cellAt, getFullCard) {
  if (!cells || cells.length === 0) return false;
  return cells.every(({ col, row }) => canForceSubmerge(card, meshes, cellAt, col, row, getFullCard));
}

/** Forced burrow succeeds only if EVERY occupied cell is land. */
export function canForceBurrowAll(card, cells, meshes, cellAt, getFullCard) {
  if (!cells || cells.length === 0) return false;
  return cells.every(({ col, row }) => canForceBurrow(card, meshes, cellAt, col, row, getFullCard));
}
